import type { DemoState } from "./demo";

export type PurchaseStatus =
  | "Draft"
  | "Awaiting director"
  | "Needs details"
  | "Approved"
  | "Ordered"
  | "Received";
export type Purchase = {
  id: string;
  partId: string;
  qty: number;
  reason: string;
  costCenter: string;
  delivery: string;
  specification: string;
  status: PurchaseStatus;
  clarification: string;
  approvedBy?: string;
  approvedSnapshot?: string;
  history: { at: string; actor: string; action: string }[];
};
export type AccessGrant = {
  status: "Requested" | "Granted" | "Revoked";
  scopes: string[];
  requestedBy: string;
  approvedBy?: string;
  reason: string;
  history: string[];
};
export type OpsState = {
  legacyTiming?: unknown;
  acknowledged: string[];
  materialChecks: Record<string, boolean>;
  coverage: Record<string, string>;
  connectorSelection: string[];
  purchases: Purchase[];
  grants: Record<string, AccessGrant>;
  knowledgeNotes: {
    id: string;
    title: string;
    category: string;
    body: string;
    revision: number;
  }[];
  clock: string;
};
export function initialOps(): OpsState {
  return {
    acknowledged: [],
    materialChecks: {},
    coverage: {},
    connectorSelection: [],
    purchases: [],
    grants: {},
    knowledgeNotes: [],
    clock: "2026-10-13T16:15:00Z",
  };
}
export const failureModes = [
  {
    code: "FM-D86-001",
    name: "Flask / heater contact loss",
    instrument: "DIST-04",
    effect: "Distillation temperature profile bias",
    control: "QC and thermal-response monitoring",
    source: "Local controlled FMEA catalog · rev 1",
    error: "OEM event 33 (demo mapping)",
  },
  {
    code: "FM-D93-002",
    name: "Ignition-source deterioration",
    instrument: "FLASH-01",
    effect: "Unreliable flash detection",
    control: "Ignition check before batch",
    source: "Local controlled FMEA catalog · rev 1",
    error: "No OEM code asserted",
  },
  {
    code: "FM-DEN-003",
    name: "Density-cell contamination",
    instrument: "DENS-01",
    effect: "Density bias / poor repeatability",
    control: "Blank and reference-fluid check",
    source: "Local controlled FMEA catalog · rev 1",
    error: "No OEM code asserted",
  },
];
export const materials = [
  {
    id: "MAT-CRM",
    name: "D86 reference material",
    lot: "CRM-241",
    need: 6,
    available: 14,
    unit: "vials",
    expiry: "2027-01-31",
    shift: "All shifts",
    method: "D86",
  },
  {
    id: "MAT-FLASK",
    name: "125 mL distillation flasks",
    lot: "FL-1026",
    need: 8,
    available: 12,
    unit: "clean flasks",
    expiry: "Inspection due 31 Oct",
    shift: "Day / evening",
    method: "D86",
  },
  {
    id: "MAT-N2",
    name: "Nitrogen · grade 5.0",
    lot: "N2-889",
    need: 2,
    available: 1,
    unit: "cylinders",
    expiry: "2027-04-30",
    shift: "Night",
    method: "D4629",
  },
  {
    id: "MAT-DENS",
    name: "Density reference fluid",
    lot: "DEN-064",
    need: 3,
    available: 7,
    unit: "ampoules",
    expiry: "2026-12-31",
    shift: "Evening / night",
    method: "D4052",
  },
  {
    id: "MAT-D93",
    name: "Flash-point control material",
    lot: "FP-062",
    need: 3,
    available: 5,
    unit: "vials",
    expiry: "2027-02-15",
    shift: "All shifts",
    method: "D93",
  },
  {
    id: "MAT-SOLV",
    name: "Rinse solvent",
    lot: "SOL-183",
    need: 4,
    available: 10,
    unit: "L",
    expiry: "2027-05-30",
    shift: "All shifts",
    method: "Shared",
  },
];
export const shifts = [
  {
    id: "day",
    name: "Day",
    time: "07:00–15:00",
    lead: "Luis Ortega",
    people: "Luis Ortega · Aisha Patel",
    methods: "D86 · D4052 · D93",
    demand: "24 tests + six retests",
    ready: true,
  },
  {
    id: "evening",
    name: "Evening",
    time: "15:00–23:00",
    lead: "Nadia Rahman",
    people: "Nadia Rahman · Owen Price",
    methods: "D86 · D93 · D4629",
    demand: "28 tests · 2 customer orders",
    ready: true,
  },
  {
    id: "night",
    name: "Night",
    time: "23:00–07:00 +1",
    lead: "Mateo Silva",
    people: "Mateo Silva · Priya Desai",
    methods: "D86 · D4052; D93 gap",
    demand: "18 tests · 1 D93 batch",
    ready: false,
  },
];
export const parts = [
  {
    id: "P-101",
    name: "125 mL distillation flask",
    vendor: "Lab supply catalog",
    instrument: "DIST-04",
    price: 86,
    stock: "6 available",
    lead: "1 business day",
    sku: "DEMO-FL125",
  },
  {
    id: "P-102",
    name: "Heating-block service kit",
    vendor: "Instrument service supplier",
    instrument: "DIST-04",
    price: 640,
    stock: "Quote required",
    lead: "3 business days",
    sku: "DEMO-HBK-04",
  },
  {
    id: "P-103",
    name: "Ignition assembly",
    vendor: "Instrument service supplier",
    instrument: "FLASH-01",
    price: 310,
    stock: "3 available",
    lead: "2 business days",
    sku: "DEMO-IGN-93",
  },
  {
    id: "P-104",
    name: "Nitrogen grade 5.0 cylinder",
    vendor: "Gas supply partner",
    instrument: "ELEM-01",
    price: 165,
    stock: "Local dispatch",
    lead: "Today · 18:00",
    sku: "DEMO-N2-50",
  },
  {
    id: "P-105",
    name: "Density-cell cleaning kit",
    vendor: "Lab supply catalog",
    instrument: "DENS-01",
    price: 124,
    stock: "12 available",
    lead: "1 business day",
    sku: "DEMO-DEN-CL",
  },
  {
    id: "P-106",
    name: "D86 reference material set",
    vendor: "Reference material supplier",
    instrument: "DIST-02",
    price: 285,
    stock: "8 sets available",
    lead: "2 business days",
    sku: "DEMO-CRM-D86",
  },
];
const catalogAdditions = [
 ['D93 flash-point control','FLASH-01',210],['Density reference standard','DENS-01',145],['Autosampler syringe set','GC-01',96],['GC inlet septa · 50 pack','GC-01',75],['Capillary column','GC-01',720],['ICP nebulizer','ICP-01',430],['ICP torch','ICP-01',580],['Sulfur calibration set','ELEM-01',355],['Quartz combustion tube','ELEM-01',460],['Vapor pressure seals','VP-01',95],['Octane reference fuel kit','OCT-01',395],['Centrifuge tubes · 12 pack','CENT-01',180],['Sample bottles · 500 mL','Receiving',68],['Tamper-evident seals','Receiving',32],['Barcode labels · 1000 roll','Receiving',44],['Solvent-resistant gloves','Bench supplies',28],['Spill absorbent kit','Bench supplies',115],['Volumetric pipette set','Bench supplies',160]
] as const;
parts.push(...catalogAdditions.map(([name,instrument,price],i)=>({id:'P-'+(107+i),name,instrument,price,vendor:i%2?'Laboratory consumables supplier':'Instrument service supplier',stock:i%4===0?'Quote required':(4+i)+' available',lead:i%4===0?'Confirm with supplier':'2 business days',sku:'CAT-'+(107+i)})));
export const enterpriseSystems = [
  {
    id: "datacor",
    name: "Datacor ERP",
    category: "ERP",
    objects: ["Customers", "Orders", "Inventory", "Purchase requisitions"],
    adapter: "API / approved export",
  },
  {
    id: "sap",
    name: "SAP S/4HANA",
    category: "ERP",
    objects: ["Business partners", "Sales orders", "Material stock"],
    adapter: "OData read endpoints",
  },
  {
    id: "oracle",
    name: "Oracle Fusion",
    category: "ERP",
    objects: ["Customers", "Order lines", "Inventory balances"],
    adapter: "REST read endpoints",
  },
  {
    id: "dynamics",
    name: "Microsoft Dynamics 365",
    category: "ERP",
    objects: ["Accounts", "Orders", "Products"],
    adapter: "Dataverse read scopes",
  },
  {
    id: "netsuite",
    name: "Oracle NetSuite",
    category: "ERP",
    objects: ["Customers", "Sales orders", "Items"],
    adapter: "REST / SuiteQL read role",
  },
  {
    id: "infor",
    name: "Infor CloudSuite",
    category: "ERP",
    objects: ["Orders", "Materials", "Suppliers"],
    adapter: "ION API read role",
  },
  {
    id: "epicor",
    name: "Epicor Kinetic",
    category: "ERP",
    objects: ["Jobs", "Customers", "Part stock"],
    adapter: "REST read role",
  },
  {
    id: "sage",
    name: "Sage X3",
    category: "ERP",
    objects: ["Customers", "Products", "Orders"],
    adapter: "API / approved export",
  },
  {
    id: "odoo",
    name: "Odoo",
    category: "ERP",
    objects: ["Contacts", "Orders", "Stock"],
    adapter: "Read-only service account",
  },
  {
    id: "acumatica",
    name: "Acumatica",
    category: "ERP",
    objects: ["Customers", "Sales orders", "Inventory"],
    adapter: "REST read role",
  },
  {
    id: "ifs",
    name: "IFS Cloud",
    category: "ERP",
    objects: ["Assets", "Work orders", "Inventory"],
    adapter: "Projection read endpoints",
  },
  {
    id: "syspro",
    name: "SYSPRO",
    category: "ERP",
    objects: ["Customers", "Stock", "Orders"],
    adapter: "Business-object read role",
  },
  {
    id: "teams",
    name: "Microsoft Teams",
    category: "Collaboration",
    objects: ["Selected team/channel metadata", "Approved channel messages"],
    adapter: "Microsoft Graph · admin consent",
  },
  {
    id: "sharepoint",
    name: "SharePoint",
    category: "Documents",
    objects: ["Selected site documents", "Document metadata"],
    adapter: "Microsoft Graph · selected-site access",
  },
  {
    id: "lims",
    name: "LIMS / ELN",
    category: "Laboratory",
    objects: ["Samples", "Methods", "Authorized results"],
    adapter: "Vendor API / approved export",
  },
  {
    id: "generic",
    name: "Other ERP / SQL source",
    category: "ERP",
    objects: ["Approved views", "Approved CSV exports"],
    adapter: "Read replica / restricted SELECT",
  },
];
export const extraConnectors = [
  ["HPLC", "Agilent / Waters", "Chromatography", "LC-01"],
  ["LC-MS/MS", "SCIEX / Thermo Fisher", "Mass spectrometry", "LCMS-01"],
  ["GC-MS", "Agilent / Shimadzu", "Mass spectrometry", "GCMS-01"],
  ["ICP-MS", "Agilent / PerkinElmer", "Elemental analysis", "ICPMS-01"],
  ["UV-Vis", "Shimadzu / Thermo Fisher", "Spectroscopy", "UV-01"],
  ["FTIR", "Bruker / Thermo Fisher", "Spectroscopy", "FTIR-02"],
  ["Karl Fischer titrator", "Metrohm / Mettler Toledo", "Titration", "KF-01"],
  ["Automatic titrator", "Metrohm", "Titration", "TIT-01"],
  ["Viscometer", "Cannon / Anton Paar", "Physical properties", "VISC-01"],
  [
    "pH / conductivity meter",
    "Mettler Toledo / Hach",
    "Electrochemistry",
    "PH-01",
  ],
  ["TOC analyzer", "Shimadzu / Sievers", "Water analysis", "TOC-01"],
  ["Particle counter", "Beckman Coulter", "Particle analysis", "PART-01"],
  ["XRF spectrometer", "Rigaku / Bruker", "Elemental analysis", "XRF-01"],
  ["Refractometer", "Anton Paar", "Physical properties", "REF-01"],
  [
    "DSC / TGA",
    "TA Instruments / Mettler Toledo",
    "Thermal analysis",
    "THERM-01",
  ],
  ["Environmental logger", "Multi-vendor", "Lab environment", "ENV-01"],
].map(([name, maker, method, id]) => ({
  id,
  name,
  maker,
  method,
  protocol: "API / file / serial assessment",
  repo: null,
  signal: "Result, method, sample ID and device metadata",
  status: "Planned",
  latency: "—",
}));

export function purchaseStep(
  p: Purchase,
  action: "submit" | "clarify" | "resubmit" | "approve" | "order" | "receive",
  role: string,
  note = "",
): Purchase {
  const n = structuredClone(p);
  const allowed = (roles: string[]) => {
    if (!roles.includes(role))
      throw new Error("This action requires " + roles.join(" or ") + ".");
  };
  const requireState = (...states: PurchaseStatus[]) => {
    if (!states.includes(n.status))
      throw new Error("Action unavailable while request is " + n.status + ".");
  };
  const complete = () => {
    if (
      !n.reason.trim() ||
      !n.costCenter.trim() ||
      !n.delivery.trim() ||
      !n.specification.trim() ||
      n.qty < 1
    )
      throw new Error(
        "Add justification, cost center, delivery, specification and a positive quantity.",
      );
  };
  if (action === "submit") {
    allowed(["Lab Manager"]);
    requireState("Draft");
    complete();
    n.status = "Awaiting director";
  }
  if (action === "clarify") {
    allowed(["Procurement"]);
    requireState("Awaiting director");
    if (!note.trim()) throw new Error("Specify which details are missing.");
    n.clarification = note;
    n.status = "Needs details";
  }
  if (action === "resubmit") {
    allowed(["Lab Manager"]);
    requireState("Needs details");
    complete();
    n.status = "Awaiting director";
  }
  if (action === "approve") {
    allowed(["Lab Director"]);
    requireState("Awaiting director");
    complete();
    n.status = "Approved";
    n.approvedBy = "Dr. Elena Brooks";
    n.approvedSnapshot = JSON.stringify({
      partId: n.partId,
      qty: n.qty,
      reason: n.reason,
      costCenter: n.costCenter,
      delivery: n.delivery,
      specification: n.specification,
    });
  }
  if (action === "order") {
    allowed(["Procurement"]);
    requireState("Approved");
    if (
      n.approvedSnapshot !==
      JSON.stringify({
        partId: n.partId,
        qty: n.qty,
        reason: n.reason,
        costCenter: n.costCenter,
        delivery: n.delivery,
        specification: n.specification,
      })
    )
      throw new Error(
        "Request changed since approval; new director approval is required.",
      );
    n.status = "Ordered";
  }
  if (action === "receive") {
    allowed(["Procurement"]);
    requireState("Ordered");
    n.status = "Received";
  }
  n.history.push({
    at: new Date().toISOString(),
    actor: role,
    action: action + (note ? ": " + note : ""),
  });
  return n;
}
export function materialAvailable(s: DemoState, id: string) {
  const m = materials.find((x) => x.id === id)!;
  return (
    m.available +
    (id === "MAT-N2"
      ? s.ops.purchases
          .filter((p) => p.partId === "P-104" && p.status === "Received")
          .reduce((n, p) => n + p.qty, 0)
      : 0)
  );
}
export function readiness(s: DemoState) {
  return {
    materials: materials.filter(
      (m) => s.ops.materialChecks[m.id] && materialAvailable(s, m.id) >= m.need,
    ).length,
    people: 2 + (s.ops.coverage.night ? 1 : 0),
  };
}
